"use client";

import { c as _c } from "react/compiler-runtime";
import type { ScheduledMembershipConfirmation } from "../purchase-flow";
import React, { useMemo } from "react";
import { FaCheckCircle } from "react-icons/fa";
import Link from "next/link";
import { PatientPortalSignUpPrompt } from "@/components/patient/patient-portal-signup-prompt";
import { Button } from "@/components/ui";
import type { PostSignupBenefitTarget } from "@/lib/post-signup-service-benefit";
import { postSignupSelfScheduleHref } from "@/lib/post-signup-service-benefit";
import { cx } from "@/lib/tailwind-utils";
import { usePostSignupServiceBenefit } from "@/lib/use-post-signup-service-benefit";
interface CompleteStageProps {
  tenantSlug: string;
  purchasedServiceIds?: string[];
  packageBenefitTargets?: PostSignupBenefitTarget[];
  scheduledMembership?: ScheduledMembershipConfirmation | null;
  onShopAgain: () => void | Promise<void>;
  isShopAgainLoading?: boolean;
  shopAgainError?: boolean;
}

/**
 * Order-level post-payment confirmation. Item-specific follow-up belongs in
 * actions, such as booking a prepaid service, rather than separate success
 * messages for every item type in a mixed cart.
 */
const CompleteStage: React.FC<CompleteStageProps> = t0 => {
  const $ = _c(47);
  const {
    tenantSlug,
    purchasedServiceIds: t1,
    packageBenefitTargets: t2,
    scheduledMembership: t3,
    onShopAgain,
    isShopAgainLoading: t4,
    shopAgainError: t5
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const purchasedServiceIds = t6;
  let t7;
  if ($[2] !== t2) {
    t7 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const packageBenefitTargets = t7;
  const scheduledMembership = t3 === undefined ? null : t3;
  const isShopAgainLoading = t4 === undefined ? false : t4;
  const shopAgainError = t5 === undefined ? false : t5;
  const {
    benefit
  } = usePostSignupServiceBenefit(packageBenefitTargets);
  let t8;
  bb0: {
    if (benefit) {
      let t9;
      if ($[4] !== tenantSlug) {
        t9 = postSignupSelfScheduleHref(tenantSlug);
        $[4] = tenantSlug;
        $[5] = t9;
      } else {
        t9 = $[5];
      }
      const t10 = benefit.serviceName ? `Book ${benefit.serviceName}` : "Book an appointment";
      let t11;
      if ($[6] !== t10 || $[7] !== t9) {
        t11 = {
          href: t9,
          label: t10
        };
        $[6] = t10;
        $[7] = t9;
        $[8] = t11;
      } else {
        t11 = $[8];
      }
      t8 = t11;
      break bb0;
    }
    if (purchasedServiceIds.length === 0) {
      t8 = null;
      break bb0;
    }
    let t10;
    let t9;
    if ($[9] !== purchasedServiceIds || $[10] !== tenantSlug) {
      const params = new URLSearchParams();
      for (const serviceId of purchasedServiceIds) {
        params.append("serviceId", serviceId);
      }
      if ($[13] !== tenantSlug) {
        t9 = postSignupSelfScheduleHref(tenantSlug);
        $[13] = tenantSlug;
        $[14] = t9;
      } else {
        t9 = $[14];
      }
      t10 = params.toString();
      $[9] = purchasedServiceIds;
      $[10] = tenantSlug;
      $[11] = t10;
      $[12] = t9;
    } else {
      t10 = $[11];
      t9 = $[12];
    }
    const t11 = `${t9}?${t10}`;
    const t12 = purchasedServiceIds.length === 1 ? "Book your purchased service" : "Book your purchased services";
    let t13;
    if ($[15] !== t11 || $[16] !== t12) {
      t13 = {
        href: t11,
        label: t12
      };
      $[15] = t11;
      $[16] = t12;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    t8 = t13;
  }
  const bookingAction = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cx("motion-preset-slide-up mx-auto flex max-w-md flex-col items-center gap-4 rounded-2xl", "border border-border bg-background p-8 text-center shadow-sm");
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex h-16 w-16 items-center justify-center rounded-full bg-status-success-bg"><FaCheckCircle className="h-9 w-9 text-status-success-text" /></div>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = scheduledMembership ? "Your membership is scheduled" : "Your purchase is complete";
  let t12;
  if ($[20] !== t11) {
    t12 = <h2 className="text-h1 text-foreground">{t11}</h2>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== scheduledMembership) {
    t13 = scheduledMembership ? <div className="space-y-2"><p className="text-body text-foreground-secondary">{`${scheduledMembership.name} will activate automatically on ${scheduledMembership.activationTiming}. Benefits and billing begin then—there's nothing else you need to do.`}</p><p className="text-caption text-foreground-muted">Your purchase is complete. We'll send your receipt shortly.</p></div> : <p className="text-foreground-secondary">We'll send your receipt shortly.</p>;
    $[22] = scheduledMembership;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <div className="flex flex-col gap-1">{t12}{t13}</div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== shopAgainError) {
    t15 = shopAgainError && <p role="alert" className="text-caption text-status-error-text">We couldn't refresh the shop. Try again to see current prices and promotions.</p>;
    $[27] = shopAgainError;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== bookingAction) {
    t16 = bookingAction ? <Button asChild={true} variant="primary" className="w-full cursor-pointer"><Link href={bookingAction.href}>{bookingAction.label}</Link></Button> : null;
    $[29] = bookingAction;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = bookingAction ? "ghost" : "primary";
  const t18 = isShopAgainLoading ? "Refreshing shop" : undefined;
  const t19 = shopAgainError ? "Try again" : "Shop again";
  let t20;
  if ($[31] !== t19) {
    t20 = <span>{t19}</span>;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== isShopAgainLoading || $[34] !== onShopAgain || $[35] !== t17 || $[36] !== t18 || $[37] !== t20) {
    t21 = <Button variant={t17} onClick={onShopAgain} isLoading={isShopAgainLoading} loadingText="Refreshing shop" aria-label={t18} className="w-full cursor-pointer">{t20}</Button>;
    $[33] = isShopAgainLoading;
    $[34] = onShopAgain;
    $[35] = t17;
    $[36] = t18;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t16 || $[40] !== t21) {
    t22 = <div className="flex w-full flex-col gap-2">{t16}{t21}</div>;
    $[39] = t16;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <PatientPortalSignUpPrompt className="w-full" />;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t22) {
    t24 = <div className={t9}>{t10}{t14}{t15}{t22}{t23}</div>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t22;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
};
export default CompleteStage;